import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '@/store';
import { logoutUser } from '@/store/slices/authSlice';
import { toggleSidebar, toggleTheme } from '@/store/slices/uiSlice';
import { Home, Menu, Sun, Moon, User, LogOut, Bell, X, Building } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { fetchNotifications, markNotificationAsRead, Notification } from '@/services/notificationService';
import { supabase } from '@/utils/supabaseClient';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/services/authService';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Use both auth sources for maximum reliability
  const supabaseUser = useAuth();
  const { user: reduxUser, isAuthenticated: reduxAuth } = useSelector((state: RootState) => state.auth);
  const { theme, sidebarOpen } = useSelector((state: RootState) => state.ui);
  
  // User is authenticated if either source has user data
  const isAuthenticated = !!supabaseUser || reduxAuth;
  const user = supabaseUser || reduxUser;

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchNotifications(user.id).then(setNotifications).catch(() => {});
      // Real-time notifications subscription
      const channel = supabase
        .channel('notifications-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            if (payload.new) {
              setNotifications((prev) => [payload.new as Notification, ...prev]);
            }
          }
        )
        .subscribe();
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isAuthenticated, user]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const handleMarkAsRead = async (id: string) => {
    await markNotificationAsRead(id);
    setNotifications(notifications => notifications.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await signOut();
      
      // Clear Redux state
      dispatch(logoutUser());
      
      // Redirect to home
      navigate('/');
      
      // Force page refresh to ensure all state is cleared
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear Redux state and redirect on error
      dispatch(logoutUser());
      navigate('/');
    }
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Menu Toggle */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => dispatch(toggleSidebar())}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 lg:hidden transition-colors"
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <Home className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Villashunters
              </span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Home
            </Link>
            <Link
              to="/properties"
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Properties
            </Link>
            <Link
              to="/about"
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Contact
            </Link>
            {isAuthenticated && (
              <Link
                to={
                  user?.role === 'admin' ? '/admin-dashboard' :
                  user?.role === 'landlord' ? '/landlord-dashboard' :
                  user?.role === 'tenant' ? '/tenant-dashboard' :
                  '/dashboard'
                }
                className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={handleThemeToggle}
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            {/* Notification Bell */}
            {isAuthenticated && (
              <div className="relative" ref={dropdownRef}>
                <button
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 relative"
                  aria-label="Notifications"
                  onClick={() => setShowDropdown(v => !v)}
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                      {unreadCount}
                    </span>
                  )}
                </button>
                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    <div className="p-4 border-b border-gray-100 dark:border-gray-700 font-semibold text-gray-900 dark:text-white">
                      Notifications
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                      {notifications.length === 0 && (
                        <div className="p-4 text-gray-500 text-center">No notifications</div>
                      )}
                      {notifications.map(n => (
                        <button
                          key={n.id}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${n.read ? 'text-gray-500' : 'text-gray-900 dark:text-white font-medium'}`}
                          onClick={() => handleMarkAsRead(n.id)}
                        >
                          {n.message}
                          <span className="block text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isAuthenticated ? (
              <div className="flex items-center space-x-1 md:space-x-2">
                <Link
                  to={
                    user?.role === 'admin' ? '/admin-dashboard' :
                    user?.role === 'landlord' ? '/landlord-dashboard' :
                    user?.role === 'tenant' ? '/tenant-dashboard' :
                    '/dashboard'
                  }
                  className="hidden sm:flex items-center space-x-2 px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
                <Link
                  to={
                    user?.role === 'admin' ? '/admin-dashboard' :
                    user?.role === 'landlord' ? '/landlord-dashboard' :
                    user?.role === 'tenant' ? '/tenant-dashboard' :
                    '/dashboard'
                  }
                  className="sm:hidden p-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                  aria-label="Dashboard"
                >
                  <User className="h-5 w-5" />
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Logout"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-1 md:space-x-2">
                <Link
                  to="/login"
                  className="hidden sm:block px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/login"
                  className="sm:hidden p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  aria-label="Login"
                >
                  <User className="h-5 w-5" />
                </Link>
                <Link
                  to="/list-property"
                  className="hidden sm:block px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                >
                  List Property
                </Link>
                <Link
                  to="/list-property"
                  className="sm:hidden p-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                  aria-label="List Property"
                >
                  <Building className="h-5 w-5" />
                </Link>
                <Link
                  to="/register"
                  className="hidden sm:block px-4 py-2 rounded-md border border-primary-600 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 